/**
 * Stage L.4.d — persistence layer for tracker edits in the layout viewer.
 *
 * Three optimistic mutations against ``POST /api/runs/{run_id}/ops``,
 * each one:
 *   1. Cancels in-flight refetches on ``['run-layout', runId]``.
 *   2. Snapshots the current cache for rollback.
 *   3. Applies the change optimistically via ``setQueryData``.
 *   4. POSTs the op to the server.
 *   5. On success: for add, swaps the ``temp-<uuid>`` id for the real
 *      ``t-NNNN`` returned by the server (move/delete have nothing to
 *      finalize). Does NOT invalidate the query — the optimistic
 *      update IS the new state, and a refetch would cause flicker.
 *   6. On error: restores the snapshot and toasts a short message.
 *
 * WASM-side rollback (erase the temp block-ref, restore an erased
 * tracker, etc.) is the caller's responsibility — the viewer wraps
 * ``mutateAsync`` in try/catch and undoes its WASM mutation on the
 * catch path. Keeps the SDK-specific logic out of this hook.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { apiClient } from "../api/client";
import { projectsQueryKeys } from "./useProjects";
import type { RunLayout, RunLayoutTracker } from "../types/projects";

interface AddVars {
  /** Optimistic placeholder id (``temp-<uuid>``). Mutation onMutate
   * inserts a tracker with this id; onSuccess swaps it for the
   * server's real ``t-NNNN``. */
  tempId: string;
  sw_x: number;
  sw_y: number;
}

interface MoveVars {
  id: string;
  sw_x: number;
  sw_y: number;
}

interface DeleteVars {
  id: string;
}

interface OpResponse {
  tracker: RunLayoutTracker;
}

export function useEditOps(runId: string | undefined) {
  const qc = useQueryClient();
  const key = runId ? projectsQueryKeys.runLayout(runId) : null;

  const addTracker = useMutation<
    RunLayoutTracker,
    Error,
    AddVars,
    { snapshot: RunLayout | undefined }
  >({
    mutationFn: async ({ sw_x, sw_y }) => {
      if (!runId) throw new Error("no runId");
      const r = await apiClient.post<OpResponse>(
        `/api/runs/${runId}/ops`,
        { type: "add_tracker", sw_x, sw_y },
      );
      return r.data.tracker;
    },
    onMutate: async ({ tempId, sw_x, sw_y }) => {
      if (!key) return { snapshot: undefined };
      await qc.cancelQueries({ queryKey: key });
      const snapshot = qc.getQueryData<RunLayout>(key);
      qc.setQueryData<RunLayout>(key, (old) =>
        old
          ? { ...old, trackers: [...old.trackers, { id: tempId, sw_x, sw_y }] }
          : old,
      );
      return { snapshot };
    },
    onSuccess: (real, { tempId }) => {
      if (!key) return;
      qc.setQueryData<RunLayout>(key, (old) =>
        old
          ? {
              ...old,
              trackers: old.trackers.map((t) =>
                t.id === tempId ? real : t,
              ),
            }
          : old,
      );
    },
    onError: (_err, _vars, ctx) => {
      if (key && ctx?.snapshot) qc.setQueryData(key, ctx.snapshot);
      toast.error("Add failed — change rolled back");
    },
  });

  const moveTracker = useMutation<
    RunLayoutTracker,
    Error,
    MoveVars,
    { snapshot: RunLayout | undefined }
  >({
    mutationFn: async ({ id, sw_x, sw_y }) => {
      if (!runId) throw new Error("no runId");
      const r = await apiClient.post<OpResponse>(
        `/api/runs/${runId}/ops`,
        { type: "move_tracker", id, sw_x, sw_y },
      );
      return r.data.tracker;
    },
    onMutate: async ({ id, sw_x, sw_y }) => {
      if (!key) return { snapshot: undefined };
      await qc.cancelQueries({ queryKey: key });
      const snapshot = qc.getQueryData<RunLayout>(key);
      qc.setQueryData<RunLayout>(key, (old) =>
        old
          ? {
              ...old,
              trackers: old.trackers.map((t) =>
                t.id === id ? { ...t, sw_x, sw_y } : t,
              ),
            }
          : old,
      );
      return { snapshot };
    },
    // No onSuccess — the optimistic update already mirrors the server.
    onError: (_err, _vars, ctx) => {
      if (key && ctx?.snapshot) qc.setQueryData(key, ctx.snapshot);
      toast.error("Move failed — change rolled back");
    },
  });

  const deleteTracker = useMutation<
    void,
    Error,
    DeleteVars,
    { snapshot: RunLayout | undefined }
  >({
    mutationFn: async ({ id }) => {
      if (!runId) throw new Error("no runId");
      await apiClient.post(`/api/runs/${runId}/ops`, {
        type: "delete_tracker",
        id,
      });
    },
    onMutate: async ({ id }) => {
      if (!key) return { snapshot: undefined };
      await qc.cancelQueries({ queryKey: key });
      const snapshot = qc.getQueryData<RunLayout>(key);
      qc.setQueryData<RunLayout>(key, (old) =>
        old
          ? { ...old, trackers: old.trackers.filter((t) => t.id !== id) }
          : old,
      );
      return { snapshot };
    },
    onError: (_err, _vars, ctx) => {
      if (key && ctx?.snapshot) qc.setQueryData(key, ctx.snapshot);
      toast.error("Delete failed — change rolled back");
    },
  });

  return { addTracker, moveTracker, deleteTracker };
}
